import { type RootState } from 'reducers/root';
import { type PersistedState } from 'persistence/types';
import { isEqual, omit, safeJsonParse } from 'persistence/utils';
import persistenceKey, { apiKey } from './persistenceKey';

// Use this function to modify localStorage state before it gets compared to the
// previous localStorage state:
function normalizeLocalStorageStateBeforeDetectingChanges(
  localStorageState: PersistedState,
): PersistedState {
  return omit(localStorageState, {
    config: {
      updatedAtMilliseconds: undefined,
    },
  });
}

function hasChanges(
  oldState: PersistedState,
  newState: PersistedState,
): boolean {
  return !isEqual(
    normalizeLocalStorageStateBeforeDetectingChanges(oldState),
    normalizeLocalStorageStateBeforeDetectingChanges(newState),
  );
}

// Omits keys that should not be persisted:
function getPersistableState(state: RootState): PersistedState {
  return omit(state, {
    main: {
      imageViewer: undefined,
      windowDimensions: undefined,
    },
    workflows: {
      screenReaderNotifications: undefined,
      shouldInitLogRocket: undefined,
      widget_enabled: undefined,
      workflowsConfig: {
        widget_enabled: undefined,
      },
    },
  });
}

export function persist(state: RootState): void {
  if (!persistenceKey) {
    return;
  }

  const oldState = getStateFromLocalStorage();
  const persistableState = getPersistableState(state);

  // Persist state only if it changed:
  if (hasChanges(oldState, persistableState)) {
    try {
      localStorage.setItem(persistenceKey, JSON.stringify(persistableState));
    } catch {
      // Continue regardless of error
    }
  }
}

export function clear(): void {
  if (!persistenceKey) {
    return;
  }

  try {
    localStorage.removeItem(persistenceKey);
  } catch {
    // Continue regardless of error
  }
}

// Removes localStorage entries by data-api-key.
// So if data-api-key is 123, all of these keys will be removed:
// `{ "data-api-key": "123" }`
// `{ "data-api-key": "123", "data-ft-workflow-tag": "tag1" }`
// `{ "data-api-key": "123", "data-ft-workflow-tag": "tag2" }`
// `{ "data-api-key": "123", "config-ft-persistence-id": "foo" }`
export function clearAllByApiKey(): void {
  if (!apiKey) {
    return;
  }

  let localStorageKeys: string[];

  // `Object.keys(localStorage)` can throw "SecurityError: The operation is
  // insecure" if user enabled blocking of cookies:
  try {
    localStorageKeys = Object.keys(localStorage);
  } catch {
    localStorageKeys = [];
  }

  localStorageKeys
    .filter(localStorageKey => {
      let parsedLocalStorageKey;

      try {
        parsedLocalStorageKey = JSON.parse(localStorageKey);
      } catch {
        return false;
      }

      return (
        typeof parsedLocalStorageKey === 'object' &&
        parsedLocalStorageKey !== null &&
        'data-api-key' in parsedLocalStorageKey &&
        parsedLocalStorageKey['data-api-key'] === apiKey
      );
    })
    .forEach(matchedLocalStorageKey => {
      try {
        localStorage.removeItem(matchedLocalStorageKey);
      } catch {
        // Continue regardless of error
      }
    });
}

export function getStateFromLocalStorage(): PersistedState {
  if (!persistenceKey) {
    return {};
  }

  // `localStorage.getItem()` can throw "SecurityError: The operation is
  // insecure" if user enabled blocking of cookies:
  try {
    return safeJsonParse(localStorage.getItem(persistenceKey));
  } catch {
    return {};
  }
}
